import styles from './styles/Signup.module.scss';
import formStyles from './styles/Forms.module.scss';
import { useRef, useState } from 'react';
import Page from './layouts/Page';
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Link, useHistory } from 'react-router-dom';
import LoadingSpinner from './../dashboard/components/LoadingSpinner';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AuthService, SignupRequest } from '../api/auth';
import { useDispatch } from 'react-redux';
import { setToken } from '../store/slices/uiSlice';

const namePrefixes = ['Mr.', 'Mrs.', 'Ms.', 'Miss'];

const SignUp = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const fileUploadRef = useRef<HTMLInputElement>(null);
  const logoPlaceholderRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<any>(null);

  const { register, handleSubmit, watch, setError, formState, setValue, clearErrors } = useForm<SignupRequest & { termsAccepted: boolean }>({
    defaultValues: { namePrefix: namePrefixes[0] },
    mode: 'onTouched',
  });

  const signUp: SubmitHandler<SignupRequest & { termsAccepted: boolean }> = (data) => {
    if (!data.termsAccepted) {
      setError('termsAccepted', { type: 'manual', message: 'You must accept the terms and conditions to continue.' });
      return;
    }
    console.log(data);
    setIsLoading(true);

    let formData = new FormData();
    formData.append('photo', profilePhoto);
    formData.append('data', JSON.stringify(data));

    AuthService.signUp(formData)
      .then((res) => {
        dispatch(setToken(res.data));
        setIsLoading(false);
        history.push('/dashboard');
      })
      .catch((err) => {
        if (err.response.status === 400) {
          setError('email', { type: 'manual', message: 'Email address already used' }, { shouldFocus: true });
        }
        setIsLoading(false);
      });
  };

  const showPhotoUpload = () => {
    fileUploadRef.current?.click();
  };

  const handlePhotoUpload = (e: any) => {
    if (e.target.files[0]) {
      const photo = window.URL.createObjectURL(e.target.files[0]);
      setProfilePhoto(e.target.files[0]);

      if (logoPlaceholderRef.current) {
        logoPlaceholderRef.current.style.backgroundImage = `url(${photo})`;
        logoPlaceholderRef.current.innerHTML = '';
      }
    }
  };

  return (
    <Page>
      {isLoading && <LoadingSpinner attachToRoot />}
      <h1>Sign up</h1>
      <p>Create your account and get started immediatly</p>
      <Grid component="form" onSubmit={handleSubmit(signUp)} noValidate container flexDirection="column" sx={{ marginTop: '1rem' }}>
        <Divider light />

        <Typography variant="h5" sx={{ mt: 2 }}>
          Personal Information
        </Typography>
        <div className={formStyles.formControl}>
          <input
            onChange={(e) => handlePhotoUpload(e)}
            ref={fileUploadRef}
            type="file"
            accept=".png,.jpg"
            name="companyLogo"
            id="companyLogo"
            style={{ display: 'none' }}
          />
          <div ref={logoPlaceholderRef} onClick={() => showPhotoUpload()} className={styles.profilePhoto}>
            <div>Upload photo</div>
          </div>
        </div>

        <Grid item container spacing={2}>
          <Grid item xs={4} lg={2}>
            <FormControl size="small" fullWidth>
              <InputLabel shrink>Prefix</InputLabel>
              <Select
                input={<OutlinedInput label="" />}
                {...register('namePrefix', { required: { value: true, message: 'Name prefix is required' } })}
                defaultValue={namePrefixes[0]}
                error={!!formState.errors.namePrefix}
                fullWidth
              >
                {namePrefixes.map((namePrefix) => (
                  <MenuItem key={namePrefix} value={namePrefix}>
                    {namePrefix}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText error>{formState.errors.namePrefix?.message}</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={8} lg={4}>
            <TextField
              label="Full Name"
              {...register('fullName', { required: { value: true, message: 'Full name is required' } })}
              helperText={formState.errors.fullName?.message}
              error={!!formState.errors.fullName}
            />
          </Grid>
        </Grid>
        <Grid item container spacing={2}>
          <Grid item xs lg={6}>
            <TextField
              label="Title"
              {...register('title', { required: { value: true, message: 'Title is required' } })}
              helperText={formState.errors.title?.message}
              error={!!formState.errors.title}
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid item container spacing={2}>
          <Grid item xs lg={6}>
            <TextField
              label="Email Address"
              {...register('email', {
                required: { value: true, message: 'Email address is required' },
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              helperText={formState.errors.email?.message}
              error={!!formState.errors.email}
            />
          </Grid>
        </Grid>
        <Grid item container spacing={2}>
          <Grid item xs lg={6}>
            <TextField
              label="Phone No."
              {...register('phoneNumber', { required: { value: true, message: 'Phone number is required' } })}
              helperText={formState.errors.phoneNumber?.message}
              error={!!formState.errors.phoneNumber}
            />
          </Grid>
        </Grid>

        <Grid item container spacing={2}>
          <Grid item xs={6} lg={3}>
            <TextField
              label="Password"
              InputProps={{ type: 'password' }}
              {...register('password', { required: { value: true, message: 'Password is required' } })}
              helperText={formState.errors.password?.message}
              error={!!formState.errors.password}
            />
          </Grid>
          <Grid item xs={6} lg={3}>
            <TextField
              label="Confirm Password"
              InputProps={{ type: 'password' }}
              {...register('confirmPassword', {
                required: { value: true, message: 'Confirm Password is required' },
                validate: (value) => value === watch('password') || 'Passwords do not match',
              })}
              helperText={formState.errors.confirmPassword?.message}
              error={!!formState.errors.confirmPassword}
            />
          </Grid>
        </Grid>

        <Divider light sx={{ mt: 4 }} />

        <Typography variant="h5" sx={{ mt: 4 }}>
          Business Information
        </Typography>

        <Grid item container spacing={2}>
          <Grid item xs lg={6}>
            <TextField
              label="Company Name"
              {...register('companyName', { required: { value: true, message: 'Company name is required' } })}
              helperText={formState.errors.companyName?.message}
              error={!!formState.errors.companyName}
            />
          </Grid>
        </Grid>

        <Grid item container spacing={2}>
          <Grid item xs lg={6}>
            <TextField
              label="Company Website"
              {...register('companyWebsite', { required: { value: true, message: 'Company website is required' } })}
              helperText={formState.errors.companyWebsite?.message}
              error={!!formState.errors.companyWebsite}
            />
          </Grid>
        </Grid>

        <Grid item container spacing={2}>
          <Grid item xs lg={6}>
            <TextField
              label="Company Phone No."
              {...register('companyPhoneNumber', { required: { value: true, message: 'Company phone number is required' } })}
              helperText={formState.errors.companyPhoneNumber?.message}
              error={!!formState.errors.companyPhoneNumber}
            />
          </Grid>
        </Grid>

        <Grid item container spacing={2}>
          <Grid item xs={6} lg={3}>
            <TextField label="Tax ID" {...register('taxId')} />
          </Grid>
          <Grid item xs={6} lg={3}>
            <TextField label="VAT ID" {...register('vatId')} />
          </Grid>
        </Grid>

        <Grid item container spacing={2}>
          <Grid item xs lg={6}>
            <TextField multiline rows={3} label="Address" />
          </Grid>
        </Grid>

        <FormGroup sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                onChange={(v) => {
                  setValue('termsAccepted', v.target.checked, { shouldValidate: true });
                  clearErrors('termsAccepted');
                }}
              />
            }
            label={
              <>
                By clicking on checkbox you agree to our <Link to="/terms-and-conditions">Terms and Conditions</Link>
              </>
            }
          />
          <FormHelperText error>{formState.errors.termsAccepted?.message}</FormHelperText>
        </FormGroup>

        <Grid item container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <button disabled={isLoading} type="submit" className={formStyles.submit} style={{ width: '100%' }}>
              {isLoading ? 'Creating account...' : 'Create My Account'}
            </button>
          </Grid>
        </Grid>
      </Grid>
    </Page>
  );
};

export default SignUp;
